// client/src/components/VisibilityOverview.js
// Rebuilt Jun 2026 — Content Health styling
// Single scrolling page: tiles → trend+brand table → prompt table
// Engine breakdown in right-hand drawers (same pattern as ContentHealthTable)
// Jun 26 2026: trend chart capped at 440px, brand table capped at 440px with sticky header + scrollbar
// Jul 7 2026: Fixed visibility tile (targetMetrics.visibilityScore fallback) + brand table column alignment
// FIXED Aug 10 2026: Citation rate now calculated from campaign.urls domain-matching (same as CampaignScorecard)
// AUG 14 2026: Added CSV download links next to Brand performance and Prompts table headings
// AUG 18 2026: Added competitor visibility toggle to Prompts table
import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

if (typeof document !== 'undefined') {
  if (!document.querySelector('link[href*="tabler-icons"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css';
    document.head.appendChild(link);
  }
  if (!document.querySelector('link[href*="Work+Sans"]')) {
    const fl = document.createElement('link');
    fl.href = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap';
    fl.rel = 'stylesheet';
    document.head.appendChild(fl);
  }
}

const colors = {
  primary: '#39393a',
  secondary: '#048ba8',
  tertiary: '#e9c46a',
  accent: '#f4a261',
  grey: '#e76f51',
  green: '#01796f'
};

const ENGINE_COLORS = {
  chatgpt:    '#10a37f',
  claude:     '#cc785c',
  gemini:     '#4285f4',
  perplexity: '#1fb8cd'
};

const ENGINE_LABELS = {
  chatgpt: 'ChatGPT', claude: 'Claude', gemini: 'Gemini', perplexity: 'Perplexity'
};

function band(score) {
  if (score === null || score === undefined) return { bg: '#f3f4f6', text: '#9ca3af', bar: '#d1d5db' };
  if (score >= 70) return { bg: 'rgba(1,121,111,0.08)', text: '#01796f', bar: '#01796f' };
  if (score >= 45) return { bg: 'rgba(233,196,106,0.15)', text: '#854F0B', bar: '#e9c46a' };
  return { bg: 'rgba(231,111,81,0.08)', text: '#993C1D', bar: '#e76f51' };
}

function ScoreCell({ score }) {
  const b = band(score);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px', padding: '3px 8px', borderRadius: '6px', backgroundColor: b.bg, color: b.text, fontSize: '13px', fontWeight: '600' }}>
      {score === null || score === undefined ? '—' : `${score}%`}
    </div>
  );
}

function renderMarkdown(text) {
  if (!text) return 'No response text available.';
  let html = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/\n/g, '<br />');
  return html;
}

// ---- CSV Download Helper ----
function downloadCSV(filename, rows) {
  const csv = rows.map(r => r.map(cell => {
    const str = String(cell || '');
    return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
  }).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// ---- Drawer shell ----
function Drawer({ open, onClose, title, badge, badgeBg, badgeColor, subtitle, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000 }} />
      <div role="dialog" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(520px, 92vw)', backgroundColor: 'white', boxShadow: '-8px 0 24px rgba(0,0,0,0.18)', zIndex: 1001, display: 'flex', flexDirection: 'column', fontFamily: "'Work Sans', sans-serif" }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '17px', fontWeight: '700', color: colors.primary }}>{title}</span>
              {badge && <span style={{ padding: '3px 10px', borderRadius: '8px', backgroundColor: badgeBg, color: badgeColor, fontSize: '12px', fontWeight: '700' }}>{badge}</span>}
            </div>
            {subtitle && <div style={{ fontSize: '12px', color: '#6b7280' }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1, flexShrink: 0 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
          {children}
        </div>
      </div>
    </>
  );
}

// ---- Comparison modal ----
function ComparisonModal({ open, onClose, onExport, loading }) {
  const [period1Start, setPeriod1Start] = React.useState('');
  const [period1End, setPeriod1End] = React.useState('');
  const [period2Start, setPeriod2Start] = React.useState('');
  const [period2End, setPeriod2End] = React.useState('');
  const handleExport = () => {
    if (!period1Start || !period1End || !period2Start || !period2End) {
      alert('Please fill in all date fields');
      return;
    }
    onExport(
      new Date(period1Start),
      new Date(period1End),
      new Date(period2Start),
      new Date(period2End)
    );
  };
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1050 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(480px, 90vw)', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 1051, padding: '24px', fontFamily: "'Work Sans', sans-serif" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#39393a', margin: 0 }}>Compare Periods</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#9ca3af' }}>×</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Period 1 Start</label>
            <input type="date" value={period1Start} onChange={(e) => setPeriod1Start(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Period 1 End</label>
            <input type="date" value={period1End} onChange={(e) => setPeriod1End(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Period 2 Start</label>
            <input type="date" value={period2Start} onChange={(e) => setPeriod2Start(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Period 2 End</label>
            <input type="date" value={period2End} onChange={(e) => setPeriod2End(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: 'white', color: '#39393a', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }} disabled={loading}>
            Cancel
          </button>
          <button onClick={handleExport} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#048ba8', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }} disabled={loading}>
            {loading ? 'Generating...' : 'Download CSV'}
          </button>
        </div>
      </div>
    </>
  );
}

// ---- Engine breakdown drawer body ----
function EngineDrawerBody({ enginePerformance, brandName, isPromptDrawer, promptData }) {
  const engines = ['chatgpt', 'claude', 'gemini', 'perplexity'];
  if (isPromptDrawer && promptData) {
    return (
      <>
        {engines.map(engine => {
          const responses = (promptData.responses || []).filter(r => r.engine === engine && !r.error);
          if (responses.length === 0) return null;
          return (
            <div key={engine} style={{ marginBottom: '16px', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              {/* Engine header */}
              <div style={{ padding: '10px 14px', backgroundColor: `${ENGINE_COLORS[engine]}12`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: ENGINE_COLORS[engine] }} />
                  <span style={{ fontSize: '13px', fontWeight: '700', color: colors.primary }}>{ENGINE_LABELS[engine]}</span>
                </div>
              </div>
              {/* Responses for this engine */}
              {responses.map((resp, idx) => {
                const isBrandMentioned = resp.brandAnalysis?.targetBrandMentioned;
                return (
                  <div key={idx}>
                    {/* Response text */}
                    <div style={{ padding: '12px 14px', backgroundColor: 'white', borderBottom: idx < responses.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                      <div style={{ fontSize: '12px', lineHeight: '1.5', color: '#374151' }}
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(resp.response) }} />
                      {isBrandMentioned && (
                        <div style={{ marginTop: '8px', fontSize: '11px', fontWeight: '600', color: colors.green, backgroundColor: 'rgba(1,121,111,0.08)', padding: '4px 8px', borderRadius: '4px', display: 'inline-block' }}>
                          ✓ Your brand mentioned
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </>
    );
  }
  return (
    <>
      <div style={{ fontSize: '13px', fontWeight: '600', color: colors.primary, marginBottom: '12px' }}>Visibility by engine</div>
      {engines.map(engine => {
        const perf = enginePerformance?.[engine];
        if (!perf) return null;
        const pct = perf.brandMentionRate || 0;
        const b = band(pct);
        return (
          <div key={engine} style={{ padding: '12px', borderRadius: '8px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: ENGINE_COLORS[engine] }} />
                <span style={{ fontSize: '13px', fontWeight: '600', color: colors.primary }}>{ENGINE_LABELS[engine]}</span>
              </div>
              <span style={{ padding: '3px 8px', borderRadius: '6px', backgroundColor: b.bg, color: b.text, fontSize: '12px', fontWeight: '600' }}>{pct}%</span>
            </div>
            <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ width: pct + '%', height: '100%', background: b.bar }} />
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>{perf.totalResponses} responses</div>
          </div>
        );
      })}
    </>
  );
}

export default function VisibilityOverview({ campaignId, apiBaseUrl }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [promptAuditData, setPromptAuditData] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [citationTrendData, setCitationTrendData] = useState([]);
  const [trendMetric, setTrendMetric] = useState('visibility');
  const [promptSortOrder, setPromptSortOrder] = useState('descending');
  const [drawer, setDrawer] = useState(null);
  const [calculatedCitationRate, setCalculatedCitationRate] = useState(null);
  const [comparisonModal, setComparisonModal] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [showCompetitors, setShowCompetitors] = useState(false);

  useEffect(() => {
    if (!campaignId) { setLoading(false); return; }
    load();
  }, [campaignId]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [llmRes, histRes, campaignRes, llmHistRes] = await Promise.all([
        fetch(`${apiBaseUrl}/audit-results?campaignId=${campaignId}&moduleType=llm-audit&limit=1`),
        fetch(`${apiBaseUrl}/audit-results?campaignId=${campaignId}&moduleType=visibility-timeline&limit=30`),
        fetch(`${apiBaseUrl}/campaigns/${campaignId}`),
        fetch(`${apiBaseUrl}/audit-results?campaignId=${campaignId}&moduleType=llm-audit&limit=30`)
      ]);

      let latest = null;
      let llmD = null;
      if (llmRes.ok) {
        llmD = await llmRes.json();
        if (llmD && llmD.length > 0) latest = llmD[0].result_data;
      }
      if (!latest?.summary) {
        const fallbackRes = await fetch(`${apiBaseUrl}/audit-results?campaignId=${campaignId}&moduleType=visibility-timeline&limit=1`);
        if (fallbackRes.ok) {
          const d = await fallbackRes.json();
          if (d && d.length > 0) latest = d[0].result_data;
        }
      }
      setAuditData(latest);

      let campaign = null;
      if (campaignRes.ok) {
        campaign = await campaignRes.json();
      }
      const campaignUrls = campaign?.data?.urls || campaign?.urls || [];
      if (campaignUrls.length > 0 && llmD && llmD.length > 0) {
        const llmData = typeof llmD[0].result_data === 'string'
          ? JSON.parse(llmD[0].result_data)
          : llmD[0].result_data;
        const brandDomains = [...new Set((campaignUrls || []).map(u => {
          const urlStr = typeof u === 'string' ? u : u?.url || '';
          try {
            return new URL(urlStr).hostname.replace(/^www\./, '').toLowerCase();
          } catch {
            return null;
          }
        }).filter(Boolean))];
        const promptResults = Array.isArray(llmData.results)
          ? llmData.results
          : Array.isArray(llmData.results?.results)
            ? llmData.results.results
            : [];
        let totalWithCitations = 0;
        let totalBrandCited = 0;
        promptResults.forEach(promptResult => {
          const responses = (promptResult.responses || []).filter(r => !r.error);
          const withCitations = responses.filter(r => r.citations?.length > 0);
          totalWithCitations += withCitations.length;
          totalBrandCited += withCitations.filter(r => {
            return r.citations.some(citation => {
              const citationUrl = typeof citation === 'string'
                ? citation
                : (citation.url || citation.source || '');
              return brandDomains.some(domain => citationUrl.toLowerCase().includes(domain));
            });
          }).length;
        });
        const citationRate = totalWithCitations > 0
          ? Math.round((totalBrandCited / totalWithCitations) * 100)
          : 0;
        setCalculatedCitationRate(citationRate);
        if (llmD.length > 0) {
          setPromptAuditData(llmD[0].result_data);
        }
      }

      if (histRes.ok) {
        const hist = await histRes.json();
        if (hist && hist.length > 1) {
          const points = hist.map(r => {
            const s = r.result_data?.summary || {};
            const bm = s.brandMetrics || [];
            const pt = { date: new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }), ts: new Date(r.created_at).getTime() };
            bm.forEach(b => { pt[b.brand] = b.visibilityScore || 0; });
            return pt;
          }).sort((a, b) => a.ts - b.ts);
          setTrendData(points);
        }
      }

      if (llmHistRes.ok) {
        const hist = await llmHistRes.json();
        if (hist && hist.length > 1) {
          const points = hist.map(r => {
            const d = typeof r.result_data === 'string' ? JSON.parse(r.result_data) : r.result_data;
            const s = d.summary || {};
            const bm = s.brandMetrics || [];
            const pt = { date: new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }), ts: new Date(r.created_at).getTime() };
            bm.forEach(b => { pt[b.brand] = b.citationScore ?? 0; });
            return pt;
          }).sort((a, b) => a.ts - b.ts);
          setCitationTrendData(points);
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div style={{ padding: '48px', textAlign: 'center', fontFamily: "'Work Sans', sans-serif" }}>
      <div style={{ width: '36px', height: '36px', border: '4px solid #e5e7eb', borderTop: `4px solid ${colors.secondary}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
      <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>Loading visibility data...</p>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding: '16px', backgroundColor: '#fee2e2', borderRadius: '8px', fontFamily: "'Work Sans', sans-serif" }}>
      <span style={{ color: '#dc2626', fontSize: '14px' }}>{error}</span>
    </div>
  );

  if (!auditData?.summary) return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '48px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', fontFamily: "'Work Sans', sans-serif" }}>
      <i className="ti ti-chart-bar" style={{ fontSize: '40px', color: '#d1d5db', marginBottom: '12px', display: 'block' }} />
      <h3 style={{ fontSize: '18px', fontWeight: '600', color: colors.primary, marginBottom: '8px' }}>No visibility data yet</h3>
      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Run an audit to see how your brand appears across AI engines.</p>
    </div>
  );

  const summary = auditData.summary;
  const brandMetrics = summary.brandMetrics || [];
  const targetMetrics = brandMetrics.find(b => b.isTarget);
  const competitorMetrics = brandMetrics.filter(b => !b.isTarget);
  const enginePerformance = summary.enginePerformance || {};
  const promptResults = (promptAuditData || auditData)?.results || [];

  const tiles = [
    { key: 'visibility', label: 'Visibility', icon: 'ti-eye',         value: targetMetrics?.visibilityScore ?? summary.brandMentionRate ?? null },
    { key: 'citation',   label: 'Citation',   icon: 'ti-quote',        value: calculatedCitationRate ?? null },
    { key: 'sentiment',  label: 'Sentiment',  icon: 'ti-mood-smile',   value: targetMetrics?.averageSentiment ?? null },
    { key: 'position',   label: 'Position',   icon: 'ti-list-numbers', value: targetMetrics?.averagePosition ?? null, isPosition: true }
  ];

  const trendBrands = brandMetrics.map((b, i) => ({
    brand: b.brand,
    isTarget: b.isTarget,
    color: b.isTarget ? colors.secondary : [colors.grey, colors.tertiary, colors.accent, '#8b5cf6', '#ec4899'][i % 5]
  }));

  // Calculate competitor visibility per prompt
  const calculateCompetitorVisibilityPerPrompt = (prompt, responses) => {
    const competitorVis = {};
    competitorMetrics.forEach(comp => {
      const compResponses = (responses || []).filter(r => !r.error);
      if (compResponses.length === 0) {
        competitorVis[comp.brand] = null;
        return;
      }
      // Count mentions of competitor (this is simplified; adjust based on actual response data structure)
      const mentioned = compResponses.filter(r => {
        const respText = (r.response || '').toLowerCase();
        return respText.includes(comp.brand.toLowerCase());
      }).length;
      const pct = compResponses.length > 0 ? Math.round((mentioned / compResponses.length) * 100) : null;
      competitorVis[comp.brand] = pct;
    });
    return competitorVis;
  };

  const promptRows = promptResults.map(pr => {
    const responses = (pr.responses || []).filter(r => !r.error);
    const mentioned = responses.filter(r => r.brandAnalysis?.targetBrandMentioned).length;
    const pct = responses.length > 0 ? Math.round((mentioned / responses.length) * 100) : null;
    const competitorVis = calculateCompetitorVisibilityPerPrompt(pr.prompt, responses);
    return {
      prompt: pr.prompt || pr.promptText || '',
      pct,
      responses: pr.responses || [],
      data: pr,
      competitorVis
    };
  }).filter(r => r.prompt);

  const sortedPromptRows = [...promptRows].sort((a, b) => {
    if (promptSortOrder === 'descending') {
      return (b.pct ?? 0) - (a.pct ?? 0);
    } else {
      return (a.pct ?? 0) - (b.pct ?? 0);
    }
  });

  const openBrandDrawer = (brand) => setDrawer({ type: 'brand', brand });
  const openPromptDrawer = (row) => setDrawer({ type: 'prompt', row });
  const closeDrawer = () => setDrawer(null);

  const downloadBrandTableCSV = () => {
    const rows = [
      ['Brand', 'Mentions', 'Visibility %', 'Citation %', 'Position']
    ];
    brandMetrics.forEach(b => {
      rows.push([
        b.brand,
        b.totalMentions || 0,
        b.visibilityScore ?? '—',
        b.citationScore ?? '—',
        b.averagePosition ?? '—'
      ]);
    });
    downloadCSV(`brand-performance-${new Date().toISOString().split('T')[0]}.csv`, rows);
  };

  const downloadPromptsTableCSV = () => {
    const rows = [['Prompt', 'Visibility %']];
    if (showCompetitors) {
      rows[0] = ['Prompt', 'Brand', 'Visibility %'];
      sortedPromptRows.forEach(row => {
        rows.push([row.prompt, targetMetrics?.brand || 'Your brand', row.pct ?? '—']);
        competitorMetrics.forEach(comp => {
          rows.push([row.prompt, comp.brand, row.competitorVis[comp.brand] ?? '—']);
        });
      });
    } else {
      sortedPromptRows.forEach(row => {
        rows.push([row.prompt, row.pct ?? '—']);
      });
    }
    downloadCSV(`prompts-${new Date().toISOString().split('T')[0]}.csv`, rows);
  };

  const aggregateMetricsForPeriod = async (startDate, endDate) => {
    try {
      const llmRes = await fetch(`${apiBaseUrl}/audit-results?campaignId=${campaignId}&moduleType=llm-audit&limit=100`);
      if (!llmRes.ok) throw new Error('Failed to fetch audit results');
      const allResults = await llmRes.json();
      const filtered = allResults.filter(r => {
        const resultDate = new Date(r.created_at);
        return resultDate >= startDate && resultDate <= endDate;
      });
      if (filtered.length === 0) return null;
      const summaries = filtered.map(r => {
        const data = typeof r.result_data === 'string' ? JSON.parse(r.result_data) : r.result_data;
        return data.summary || {};
      });
      const avgVisibility = summaries.reduce((sum, s) => {
        const targetBrand = (s.brandMetrics || []).find(b => b.isTarget);
        return sum + (targetBrand?.visibilityScore || 0);
      }, 0) / summaries.length;
      const avgCitation = summaries.reduce((sum, s) => {
        const targetBrand = (s.brandMetrics || []).find(b => b.isTarget);
        return sum + (targetBrand?.citationScore || 0);
      }, 0) / summaries.length;
      const avgPosition = summaries.reduce((sum, s) => {
        const targetBrand = (s.brandMetrics || []).find(b => b.isTarget);
        return sum + (targetBrand?.averagePosition || 0);
      }, 0) / summaries.length;
      const totalResponses = summaries.reduce((sum, s) => sum + (s.totalResponses || 0), 0);
      const totalMentions = summaries.reduce((sum, s) => {
        const targetBrand = (s.brandMetrics || []).find(b => b.isTarget);
        return sum + (targetBrand?.totalMentions || 0);
      }, 0) / summaries.length;
      return {
        period: `${startDate.toLocaleDateString('en-GB')} – ${endDate.toLocaleDateString('en-GB')}`,
        visibility: avgVisibility,
        citation: avgCitation,
        position: avgPosition,
        totalResponses,
        totalMentions,
        auditCount: filtered.length
      };
    } catch (e) {
      console.error('[Comparison] Error aggregating metrics:', e);
      return null;
    }
  };

  const generateComparisonCSV = (period1, period2) => {
    const rows = [
      ['Period Comparison Report'],
      [],
      ['Metric', 'Period 1', 'Period 2', 'Change', 'Change %'],
      []
    ];
    const addRow = (label, val1, val2, isPercent = false) => {
      if (val1 === null || val2 === null) return;
      const change = val2 - val1;
      const changePct = val1 > 0 ? ((change / val1) * 100).toFixed(1) : 'N/A';
      const changeStr = change >= 0 ? `+${change.toFixed(1)}` : change.toFixed(1);
      rows.push([
        label,
        isPercent ? `${val1.toFixed(1)}%` : val1.toFixed(1),
        isPercent ? `${val2.toFixed(1)}%` : val2.toFixed(1),
        changeStr,
        changePct === 'N/A' ? 'N/A' : `${changePct}%`
      ]);
    };
    rows.push(['Visibility Score', period1.visibility, period2.visibility]);
    addRow('Visibility Score', period1.visibility, period2.visibility, true);
    addRow('Citation Score', period1.citation, period2.citation, true);
    addRow('Average Position', period1.position, period2.position);
    addRow('Total Responses', period1.totalResponses, period2.totalResponses);
    addRow('Total Mentions', period1.totalMentions, period2.totalMentions);
    rows.push([]);
    rows.push(['Period 1', period1.period, `${period1.auditCount} audits`]);
    rows.push(['Period 2', period2.period, `${period2.auditCount} audits`]);
    const csv = rows.map(r => r.map(cell => {
      const str = String(cell || '');
      return str.includes(',') ? `"${str}"` : str;
    }).join(',')).join('\n');
    return csv;
  };

  const handleComparisonExport = async (period1Start, period1End, period2Start, period2End) => {
    setComparisonLoading(true);
    try {
      const [p1, p2] = await Promise.all([
        aggregateMetricsForPeriod(period1Start, period1End),
        aggregateMetricsForPeriod(period2Start, period2End)
      ]);
      if (!p1 || !p2) {
        alert('No data found for one or both periods');
        setComparisonLoading(false);
        return;
      }
      const csv = generateComparisonCSV(p1, p2);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `${targetMetrics?.brand || 'brand'}-comparison-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      setComparisonModal(null);
    } catch (e) {
      alert('Error generating comparison: ' + e.message);
    } finally {
      setComparisonLoading(false);
    }
  };

  const BRAND_TABLE_MAX_HEIGHT = 480;
  const colWidths = { brand: '50%', vis: '17%', cit: '17%', pos: '16%' };

  return (
    <div style={{ fontFamily: "'Work Sans', sans-serif" }}>
      {/* ── Header ── */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '20px 24px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: colors.primary, margin: 0, marginBottom: '2px' }}>Visibility</h2>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
              {targetMetrics?.brand || 'Your brand'} · {summary.totalResponses || summary.successfulResponses || 0} responses · last {summary.windowDays ? `${summary.windowDays} days` : 'run'}
            </p>
          </div>
          <button
            onClick={() => setComparisonModal({ open: true })}
            style={{
              padding: '8px 14px',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
              backgroundColor: 'white',
              color: colors.secondary,
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap'
            }}
          >
            <i className="ti ti-calendar-stats" style={{ fontSize: '14px' }} />
            Compare Periods
          </button>
        </div>
      </div>

      {/* ── Four tiles ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(110px, 160px))', gap: '10px', marginBottom: '16px', justifyContent: 'start' }}>
        {tiles.map(tile => {
          const b = tile.isPosition
            ? (tile.value <= 2 ? { bg: 'rgba(1,121,111,0.08)', text: '#01796f', bar: '#01796f' } : tile.value <= 5 ? { bg: 'rgba(233,196,106,0.15)', text: '#854F0B', bar: '#e9c46a' } : { bg: 'rgba(231,111,81,0.08)', text: '#993C1D', bar: '#e76f51' })
            : band(tile.value);
          const display = tile.value === null ? '—' : tile.isPosition ? `#${typeof tile.value === 'number' ? tile.value.toFixed(1) : tile.value}` : `${tile.value}%`;
          const pct = tile.isPosition ? null : tile.value;
          return (
            <div key={tile.key} style={{ backgroundColor: 'white', border: '0.5px solid #e5e7eb', borderRadius: '10px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                <i className={`ti ${tile.icon}`} style={{ fontSize: '15px', color: tile.value !== null ? b.text : '#9ca3af' }} />
                <span style={{ fontSize: '11px', color: '#6b7280' }}>{tile.label}</span>
              </div>
              <div style={{ fontSize: '26px', fontWeight: '700', color: tile.value !== null ? b.text : '#9ca3af', marginBottom: '8px' }}>{display}</div>
              <div style={{ height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                {pct !== null && <div style={{ width: pct + '%', height: '100%', background: b.bar, borderRadius: '2px' }} />}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Trend + Brand table ── */}
      <div style={{ display: 'grid', gridTemplateColumns: (trendData.length > 1 || citationTrendData.length > 1) ? '1fr 1fr' : '1fr', gap: '16px', marginBottom: '16px', alignItems: 'start' }}>
        {(trendData.length > 1 || citationTrendData.length > 1) && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '20px 24px', height: `${BRAND_TABLE_MAX_HEIGHT}px`, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: colors.primary }}>
                {trendMetric === 'visibility' ? 'Visibility' : 'Citation'} Timeline
              </div>
              <div style={{ display: 'flex', gap: '4px', backgroundColor: '#f3f4f6', padding: '2px', borderRadius: '6px' }}>
                <button
                  onClick={() => setTrendMetric('visibility')}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '4px',
                    border: 'none',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    backgroundColor: trendMetric === 'visibility' ? 'white' : 'transparent',
                    color: trendMetric === 'visibility' ? colors.secondary : '#6b7280',
                    transition: 'all 0.2s'
                  }}
                >
                  Visibility
                </button>
                <button
                  onClick={() => setTrendMetric('citation')}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '4px',
                    border: 'none',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    backgroundColor: trendMetric === 'citation' ? 'white' : 'transparent',
                    color: trendMetric === 'citation' ? colors.accent : '#6b7280',
                    transition: 'all 0.2s'
                  }}
                >
                  Citation
                </button>
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
              {trendMetric === 'visibility' ? 'Mention rate over time across all prompts' : 'Citation rate over time across all prompts'}
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendMetric === 'visibility' ? trendData : citationTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '11px' }} />
                  <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} domain={[0, 100]} tickFormatter={v => `${v}%`} width={36} />
                  <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }} formatter={(v) => [`${v}%`]} />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                  {trendBrands.map(({ brand, isTarget, color }) => (
                    <Line key={brand} type="monotone" dataKey={brand} stroke={color} strokeWidth={isTarget ? 2.5 : 1.5} dot={{ fill: color, r: isTarget ? 4 : 2.5 }} activeDot={{ r: 6 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden', height: `${BRAND_TABLE_MAX_HEIGHT}px`, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: colors.primary }}>Brand performance</div>
              <button
                onClick={downloadBrandTableCSV}
                title="Download as CSV"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '0',
                  fontSize: '12px',
                  color: '#6b7280',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.color = colors.secondary}
                onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
              >
                <i className="ti ti-download" style={{ fontSize: '14px' }} />
                <span>CSV</span>
              </button>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Click a row to see engine breakdown</div>
          </div>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', flexShrink: 0, tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: colWidths.brand }} />
                <col style={{ width: colWidths.vis }} />
                <col style={{ width: colWidths.cit }} />
                <col style={{ width: colWidths.pos }} />
              </colgroup>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '10px 20px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Brand</th>
                  <th style={{ textAlign: 'center', padding: '10px 12px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    <i className="ti ti-eye" style={{ fontSize: '14px' }} title="Visibility" />
                  </th>
                  <th style={{ textAlign: 'center', padding: '10px 12px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    <i className="ti ti-quote" style={{ fontSize: '14px' }} title="Citation" />
                  </th>
                  <th style={{ textAlign: 'center', padding: '10px 12px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    <i className="ti ti-list-numbers" style={{ fontSize: '14px' }} title="Position" />
                  </th>
                </tr>
              </thead>
            </table>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: colWidths.brand }} />
                  <col style={{ width: colWidths.vis }} />
                  <col style={{ width: colWidths.cit }} />
                  <col style={{ width: colWidths.pos }} />
                </colgroup>
                <tbody>
                  {brandMetrics.map((brand, idx) => {
                    const isTarget = brand.isTarget;
                    const pos = brand.averagePosition ?? null;
                    return (
                      <tr key={brand.brand}
                        style={{ borderBottom: idx < brandMetrics.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', backgroundColor: 'white' }}
                        onClick={() => openBrandDrawer(brand)}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}>
                        <td style={{ padding: '10px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {isTarget && (
                              <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', color: 'white', backgroundColor: colors.secondary, textTransform: 'uppercase', flexShrink: 0 }}>YOU</span>
                            )}
                            <span style={{ fontSize: '13px', fontWeight: isTarget ? '600' : '400', color: isTarget ? colors.secondary : colors.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{brand.brand}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px', paddingLeft: isTarget ? '38px' : '0' }}>{brand.totalMentions || 0} mention{brand.totalMentions !== 1 ? 's' : ''}</div>
                        </td>
                        <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                          <ScoreCell score={brand.visibilityScore ?? null} />
                        </td>
                        <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                          <ScoreCell score={brand.citationScore ?? null} />
                        </td>
                        <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                          {pos !== null
                            ? <div style={{ fontSize: '13px', fontWeight: '600', color: pos <= 2 ? colors.green : colors.primary }}>#{pos.toFixed(1)}</div>
                            : <div style={{ fontSize: '13px', color: '#d1d5db' }}>—</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ padding: '10px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '14px', flexWrap: 'wrap', flexShrink: 0 }}>
            {[
              { label: '≥70', bg: 'rgba(1,121,111,0.08)', color: '#01796f' },
              { label: '45–69', bg: 'rgba(233,196,106,0.15)', color: '#854F0B' },
              { label: '<45', bg: 'rgba(231,111,81,0.08)', color: '#993C1D' }
            ].map(({ label, bg, color }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#6b7280' }}>
                <div style={{ width: '20px', height: '12px', borderRadius: '3px', backgroundColor: bg, border: `1px solid ${color}30` }} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Prompt table with competitor toggle ── */}
      {promptRows.length > 0 && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: colors.primary }}>Prompts</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {competitorMetrics.length > 0 && (
                  <div style={{ display: 'flex', gap: '4px', backgroundColor: '#f3f4f6', padding: '2px', borderRadius: '6px' }}>
                    <button
                      onClick={() => setShowCompetitors(false)}
                      style={{
                        padding: '5px 10px',
                        borderRadius: '4px',
                        border: 'none',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        backgroundColor: !showCompetitors ? 'white' : 'transparent',
                        color: !showCompetitors ? colors.secondary : '#6b7280',
                        transition: 'all 0.2s'
                      }}
                      title="Show brand-only visibility"
                    >
                      Brand only
                    </button>
                    <button
                      onClick={() => setShowCompetitors(true)}
                      style={{
                        padding: '5px 10px',
                        borderRadius: '4px',
                        border: 'none',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        backgroundColor: showCompetitors ? 'white' : 'transparent',
                        color: showCompetitors ? colors.secondary : '#6b7280',
                        transition: 'all 0.2s'
                      }}
                      title="Include competitors"
                    >
                      + Competitors
                    </button>
                  </div>
                )}
                <button
                  onClick={downloadPromptsTableCSV}
                  title="Download as CSV"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '0',
                    fontSize: '12px',
                    color: '#6b7280',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = colors.secondary}
                  onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
                >
                  <i className="ti ti-download" style={{ fontSize: '14px' }} />
                  <span>CSV</span>
                </button>
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Visibility per prompt · click to see engine breakdown</div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '10px 20px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Prompt</th>
                <th
                  onClick={() => setPromptSortOrder(promptSortOrder === 'descending' ? 'ascending' : 'descending')}
                  style={{ textAlign: 'center', padding: '10px 16px', color: '#6b7280', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort"
                >
                  <i className="ti ti-eye" style={{ fontSize: '14px' }} title="Visibility" />
                  <span style={{ marginLeft: '4px', fontSize: '10px', fontWeight: '700' }}>
                    {promptSortOrder === 'descending' ? '↓' : '↑'}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedPromptRows.map((row, idx) => (
                <React.Fragment key={idx}>
                  <tr
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', backgroundColor: 'white' }}
                    onClick={() => openPromptDrawer(row)}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}>
                    <td style={{ padding: '12px 20px', color: colors.primary, lineHeight: '1.4' }}>{row.prompt}</td>
                    <td style={{ textAlign: 'center', padding: '12px 16px' }}>
                      <ScoreCell score={row.pct} />
                    </td>
                  </tr>
                  {showCompetitors && competitorMetrics.map(comp => (
                    <tr key={`${idx}-${comp.brand}`}
                      style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: '#fafafa', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fafafa'}>
                      <td style={{ padding: '8px 20px 8px 48px', color: '#6b7280', fontSize: '12px', lineHeight: '1.4' }}>
                        {comp.brand}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px 16px' }}>
                        <ScoreCell score={row.competitorVis[comp.brand]} />
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Comparison Modal ── */}
      <ComparisonModal
        open={comparisonModal?.open}
        onClose={() => setComparisonModal(null)}
        onExport={handleComparisonExport}
        loading={comparisonLoading}
      />

      {/* ── Drawers ── */}
      {drawer?.type === 'brand' && (() => {
        const brand = drawer.brand;
        const b = band(brand.visibilityScore);
        const brandEnginePerf = {};
        Object.entries(enginePerformance).forEach(([engine, stats]) => {
          if (brand.isTarget) {
            brandEnginePerf[engine] = stats;
          } else {
            const mentions = stats.competitorMentions?.[brand.brand] || 0;
            const total = stats.totalResponses || 1;
            brandEnginePerf[engine] = {
              brandMentionRate: Math.round((mentions / total) * 100),
              totalResponses: stats.totalResponses
            };
          }
        });
        return (
          <Drawer open={true} onClose={closeDrawer}
            title={brand.brand}
            badge={brand.visibilityScore != null ? `${brand.visibilityScore}% visibility` : null}
            badgeBg={b.bg} badgeColor={b.text}
            subtitle={`${brand.totalMentions || 0} mentions across all prompts`}>
            <EngineDrawerBody enginePerformance={brandEnginePerf} brandName={brand.brand} />
          </Drawer>
        );
      })()}
      {drawer?.type === 'prompt' && (() => {
        const row = drawer.row;
        const b = band(row.pct);
        return (
          <Drawer open={true} onClose={closeDrawer}
            title={row.prompt}
            badge={row.pct != null ? `${row.pct}% visibility` : null}
            badgeBg={b.bg} badgeColor={b.text}>
            <EngineDrawerBody isPromptDrawer promptData={row.data} />
          </Drawer>
        );
      })()}
    </div>
  );
}
